import React, { useEffect, useState } from 'react';
import { useAppSelector as useSelector } from 'hooks/reduxHooks';

import {
  FlightSearch,
  FlightDropdown,
  FlightButton,
  FlightCollapsible,
  FlightCheckbox,
  FlightTable,
  FlightLabel,
} from '@flybits/design-system';
import DateRangePicker from 'components/Shared/DateRangePicker';
import type { AdvancedDateRange, Preset } from 'components/Shared/DateRangePicker';

import { isEqual } from 'lodash';
import moment from 'moment';

import useActions from 'hooks/useActions';
import * as AuditActions from 'store/settings/audit/audit.action';

import { Log } from 'interface/audit/audit.interface';
import AuditEmptyState from './AuditEmptyState';
import { dateToEpoch, levelToRole, formatAuditField, mapRolesToLevelIds, isDateRangeSingleDay } from './audit.utils';

import './Audit.scss';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { TExperienceCanvasRouteParams } from 'pages/ExperienceCanvas/types';

// Constants
const USER_FILTER_OPTIONS = [
  { label: 'Creator', value: 'Creator' },
  { label: 'Publisher', value: 'Publisher' },
  { label: 'Permission Manager', value: 'Permission Manager' },
  { label: 'Owner', value: 'Owner' },
  { label: 'Grantor', value: 'Grantor' },
];

const ACTION_FILTER_OPTIONS = [
  { label: 'Created', value: 'created' },
  { label: 'Updated', value: 'updated' },
  { label: 'Deleted', value: 'deleted' },
  { label: 'Activated', value: 'activated' },
  { label: 'Deactivated', value: 'deactivated' },
  { label: 'Broadcasted', value: 'broadcasted' },
  { label: 'Modified', value: 'modified' },
  { label: 'Undrafted', value: 'undrafted' },
];

const TYPE_FILTER_OPTIONS = [
  { label: 'Entity', value: 'entity' },
  { label: 'System', value: 'system' },
];

const TABLE_HEADERS = [
  { name: '', key: 'key', isVisible: false, hideTooltip: true },
  { name: 'Timestamp', key: 'timestamp', isVisible: true, hideTooltip: true, isSortable: true },
  { name: 'User', key: 'user', isVisible: true, hideTooltip: true, isSortable: false },
  { name: 'Action', key: 'action', isVisible: true, hideTooltip: true, isSortable: true },
  { name: 'Entity', key: 'entity_type', isVisible: true, hideTooltip: true, isSortable: true },
  { name: 'Service', key: 'service', isVisible: true, hideTooltip: true, isSortable: true },
  { name: 'Description', key: 'description', isVisible: true, hideTooltip: true, isSortable: false },
];

// Types
type TableData = {
  key: string;
  timestamp?: JSX.Element;
  user?: JSX.Element;
  action?: JSX.Element;
  entity_type?: JSX.Element;
  service?: JSX.Element;
  description?: JSX.Element;
};

type AuditFilters = {
  start: number;
  end: number;
  action: string[];
  userType: string[];
  eventType: string[];
  userSearchIds: string[];
  userRoleIds: string[];
  searchQuery: string;
};

type Pagination = {
  limit: number;
  offset: number;
  sort: 'asc' | 'desc';
  sortBy: string;
};

// Main component
export default function Audit() {
  const user = useSelector((state) => state.auth.user);
  const isAuditLoading = useSelector((state) => state.audit.isLoading);
  const history = useHistory();
  const today = new Date();
  const todayEpoch = (today.getTime() - today.getMilliseconds()) / 1000;
  const users = useSelector((state) => state.audit.users);
  const serviceAccounts = useSelector((state) => state.audit.serviceAccounts);
  const logs: Log[] = useSelector((state) => state.audit.logs);
  const { totalRecords } = useSelector((state) => state.audit.pagination) as { totalRecords: number };

  const [filters, setFilters] = useState<AuditFilters>({
    start: 0,
    end: todayEpoch,
    action: [],
    userType: [],
    eventType: [],
    userSearchIds: [],
    userRoleIds: [],
    searchQuery: '',
  });

  const [isSingleDay, setIsSingleDay] = useState(false);
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    limit: 20,
    offset: 0,
    sort: 'desc',
    sortBy: 'timestamp',
  });

  const auditAction = useActions(AuditActions);
  const { pid: projectId } = useRouteMatch<TExperienceCanvasRouteParams>().params;

  // Derived state
  const hasFiltersApplied = Boolean(
    filters.action.length > 0 ||
      filters.eventType.length > 0 ||
      filters.userType.length > 0 ||
      filters.userSearchIds.length > 0 ||
      filters.start > 0 ||
      filters.searchQuery.trim() !== '',
  );

  // Event handlers
  const handleCreateExperience = () => {
    history.push(`/project/${projectId}/experiences/templates/library`);
  };

  const handleOnSearch = (value: string) => {
    let searchIds: string[] = [];
    if (value !== '') {
      const userIds = users
        .filter((user) => `${user.firstName} ${user.lastName}`.toLowerCase().includes(value.toLowerCase()))
        .map((user) => user.id);
      const saIds = serviceAccounts
        .filter((sa) => sa.name.toLowerCase().includes(value.toLowerCase()))
        .map((sa) => sa.id);
      searchIds = userIds.concat(saIds);
      if (searchIds.length < 1) {
        searchIds = [''];
      }
    }
    setFilters({ ...filters, userSearchIds: searchIds, searchQuery: value });
  };

  const resetPagination = () => {
    setPagination({ limit: 20, offset: 0, sort: 'desc', sortBy: 'timestamp' });
  };

  const AUDIT_PRESETS: Preset[] = ['last7', 'last30', 'last60', 'last90', 'allTime', 'custom'];

  const handleOnSingleDatePick = (value: AdvancedDateRange) => {
    const [day] = value;
    if (!day) {
      resetPagination();
      setFilters({ ...filters, start: 0, end: todayEpoch });
      return;
    }
    resetPagination();
    setFilters({
      ...filters,
      start: dateToEpoch(day),
      end: dateToEpoch(day) + 86359,
    });
  };

  const handleOnRangeDatePick = (value: AdvancedDateRange) => {
    const [start, end, , unit] = value as [Date | null, Date | null, number?, string?];
    if (unit === 'allTime') {
      resetPagination();
      setFilters({ ...filters, start: 0, end: todayEpoch });
      return;
    }
    if (!start) {
      return;
    }
    const newEndDate = end || new Date();
    resetPagination();
    setFilters({
      ...filters,
      start: dateToEpoch(start),
      end: dateToEpoch(newEndDate),
    });
  };

  const singleDateValue: AdvancedDateRange = filters.start
    ? [new Date(filters.end * 1000), new Date(filters.end * 1000)]
    : [null, null];
  const rangeDateValue: AdvancedDateRange = filters.start
    ? [new Date(filters.start * 1000), new Date(filters.end * 1000)]
    : [null, null];

  const toggleArrayFilter = (key: 'action' | 'userType' | 'eventType', value: string) => {
    const currentArray = filters[key];
    const newArray = currentArray.includes(value) ? currentArray.filter((v) => v !== value) : [...currentArray, value];
    setFilters({ ...filters, [key]: newArray });
  };

  const handleOnHeaderSort = (header: { key?: string }) => {
    let key = pagination.sortBy;
    let sort: Pagination['sort'] = 'desc';
    if (header?.key === pagination.sortBy) {
      sort = pagination.sort === 'desc' ? 'asc' : 'desc';
    } else if (header?.key) {
      key = header.key;
    }
    setPagination({ ...pagination, sort, sortBy: key, offset: 0 });
  };

  const handleOnPageChange = (page: number) => {
    setPagination({ ...pagination, offset: pagination.limit * (page - 1) });
  };

  const handleOnRowPerPageChange = (amount: number | string) => {
    setPagination({ ...pagination, limit: Number(amount), offset: 0 });
  };

  const getTotalPageNumber = () => Math.ceil(totalRecords / pagination.limit);

  const getCurrentPageNumber = () => {
    const totalPages = getTotalPageNumber();
    return totalPages + 1 - Math.ceil((totalRecords - pagination.offset) / pagination.limit);
  };

  // Effects
  useEffect(() => {
    document.title = 'Audit History | Experience Studio @ Flybits';
    if (user && user?.level < 20) {
      history.push('/no-access');
    }
  }, [user, history]);

  useEffect(() => {
    (async () => {
      await auditAction.fetchUsers();
      await auditAction.fetchServiceAccounts();
    })();
  }, [auditAction]);

  useEffect(() => {
    (async () => {
      const roleIds = mapRolesToLevelIds(filters.userType, users);
      const updatedFilters = { ...filters, userRoleIds: roleIds };
      await auditAction.fetchAuditLogs(pagination, updatedFilters);
    })();
  }, [
    pagination,
    filters.action,
    filters.eventType,
    filters.userType,
    filters.userSearchIds,
    filters.start,
    filters.end,
    auditAction,
    users,
    filters,
  ]);

  useEffect(() => {
    const _tableData: TableData[] =
      logs?.map((log: Log) => ({
        timestamp: (
          <span className="audit__content__body__table__content">
            {moment.unix(log.timestamp).utc().format('DD/MM/YYYY kk:mm:ss')}
          </span>
        ),
        user: (
          <div className="audit__content__body__table__content">
            <span className="audit__content__body__table__name">{log.userName}</span>
            <span>{levelToRole(log.userLevel)}</span>
          </div>
        ),
        key: log.id,
        action: <span className="audit__content__body__table__content">{formatAuditField(log.action)}</span>,
        entity_type: <span className="audit__content__body__table__content">{formatAuditField(log.entityType)}</span>,
        service: <span className="audit__content__body__table__content">{formatAuditField(log.service)}</span>,
        description: <span className="audit__content__body__table__content">{log.entityName}</span>,
      })) || [];

    if (!isEqual(tableData, _tableData)) {
      setTableData(_tableData);
    }
  }, [logs, tableData]);

  // Collapsible filter items
  const collapsibleItems = [
    {
      name: 'User Role',
      key: 0,
      disabled: false,
      content: (
        <div>
          {USER_FILTER_OPTIONS.map((option) => (
            <FlightCheckbox
              key={option.value}
              checkState={filters.userType.includes(option.value) ? 'SELECTED' : 'UNSELECTED'}
              onSelect={() => toggleArrayFilter('userType', option.value)}
              label={option.label}
            />
          ))}
        </div>
      ),
    },
    {
      name: 'Action',
      key: 1,
      disabled: false,
      content: (
        <div>
          {ACTION_FILTER_OPTIONS.map((option) => (
            <FlightCheckbox
              key={option.value}
              checkState={filters.action.includes(option.value) ? 'SELECTED' : 'UNSELECTED'}
              onSelect={() => toggleArrayFilter('action', option.value)}
              label={option.label}
            />
          ))}
        </div>
      ),
    },
    {
      name: 'Type',
      key: 2,
      disabled: false,
      content: (
        <div>
          {TYPE_FILTER_OPTIONS.map((option) => (
            <FlightCheckbox
              key={option.value}
              checkState={filters.eventType.includes(option.value) ? 'SELECTED' : 'UNSELECTED'}
              onSelect={() => toggleArrayFilter('eventType', option.value)}
              label={option.label}
            />
          ))}
        </div>
      ),
    },
  ];

  // Render functions
  const renderEmptyState = (type: 'no-history' | 'no-results') => (
    <AuditEmptyState type={type} onCreateExperience={handleCreateExperience} />
  );

  return (
    <div className="audit">
      <div className="audit__header">
        <span className="audit__header__title">Audit History</span>
      </div>

      {!isAuditLoading && logs.length < 1 && !hasFiltersApplied ? (
        renderEmptyState('no-history')
      ) : (
        <div className="audit__content">
          <div className="audit__content__header">
            <FlightSearch
              className="audit__content__header__search"
              onSearch={handleOnSearch}
              placeholderText="Search by user name"
              label="Search by user name"
            />
            {isSingleDay ? (
              <DateRangePicker
                className="audit__content__header__date-picker"
                mode="single"
                value={singleDateValue}
                onChange={handleOnSingleDatePick}
                presets={[]}
                ariaLabel="Select a specific date"
              />
            ) : (
              <DateRangePicker
                className="audit__content__header__date-picker"
                mode="range"
                value={rangeDateValue}
                onChange={handleOnRangeDatePick}
                presets={AUDIT_PRESETS}
                ariaLabel="Select a date range"
              />
            )}
            <FlightCheckbox
              onSelect={() => setIsSingleDay(!isSingleDay)}
              label="Specific Date"
              checkState={isSingleDay ? 'SELECTED' : 'UNSELECTED'}
            />
            <FlightDropdown
              className="audit__content__header__filter"
              maxWidth="268px"
              trigger={
                <FlightButton
                  isPropagateUpperActions
                  onClick={() => undefined}
                  theme="secondary"
                  label="Filter"
                  iconLeft="filter"
                />
              }
            >
              <div className="audit__content__header__filter__menu">
                <span className="audit__content__header__filter__title">Filter by Categories</span>
                <FlightCollapsible
                  className="audit__content__header__filter__categories"
                  maxHeight="unset"
                  items={collapsibleItems}
                  openMultiple
                />
              </div>
            </FlightDropdown>
          </div>
          {hasFiltersApplied && (
            <div className="audit__content__filter">
              <span className="audit__content__filter__title">Selected Filters:</span>
              <div className="audit__content__filter__labels-group">
                {filters.searchQuery && filters.searchQuery.trim() !== '' && (
                  <FlightLabel label={`Search: ${filters.searchQuery}`} onRemove={() => handleOnSearch('')} />
                )}
                {filters.start > 0 &&
                  (isDateRangeSingleDay(filters.start, filters.end) ? (
                    <FlightLabel
                      label={`Date: ${moment.unix(filters.start).utc().format('DD/MM/YYYY')}`}
                      onRemove={() => {
                        resetPagination();
                        setFilters({ ...filters, start: 0, end: todayEpoch });
                      }}
                    />
                  ) : (
                    <FlightLabel
                      label={`Date: ${moment.unix(filters.start).utc().format('DD/MM/YYYY')} - ${moment
                        .unix(filters.end)
                        .utc()
                        .format('DD/MM/YYYY')}`}
                      onRemove={() => {
                        resetPagination();
                        setFilters({ ...filters, start: 0, end: todayEpoch });
                      }}
                    />
                  ))}
                {filters.action.map((filter) => (
                  <FlightLabel
                    key={'action-' + filter}
                    label={`Action: ${filter}`}
                    onRemove={() => toggleArrayFilter('action', filter)}
                  />
                ))}
                {filters.eventType.map((filter) => (
                  <FlightLabel
                    key={'type-' + filter}
                    label={`Type: ${filter}`}
                    onRemove={() => toggleArrayFilter('eventType', filter)}
                  />
                ))}
                {filters.userType.map((filter) => (
                  <FlightLabel
                    key={'role-' + filter}
                    label={`User Role: ${filter}`}
                    onRemove={() => toggleArrayFilter('userType', filter)}
                  />
                ))}
              </div>
            </div>
          )}
          <div className="audit__content__body">
            {hasFiltersApplied && logs.length < 1 ? (
              renderEmptyState('no-results')
            ) : (
              <FlightTable
                className="audit__content__body__table"
                tableHeaders={TABLE_HEADERS}
                hasPaginationBeforeTable={false}
                isLoading={isAuditLoading}
                hasPaginationAfterTable
                handleHeaderSort={handleOnHeaderSort}
                tableData={tableData}
                sortByKey={pagination.sortBy}
                sortOrder={pagination.sort}
                paginationProps={{
                  totalPageNumber: getTotalPageNumber(),
                  currentPageNumber: getCurrentPageNumber(),
                  rowsPerPageOptions: [20, 40, 60, 80, 100],
                  currentRowsPerPage: pagination.limit,
                  handlePageChange: handleOnPageChange,
                  handleRowsPerPageChange: handleOnRowPerPageChange,
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
